import { IconCheck, IconFileTypePdf, IconFileTypeXml, IconFiles, IconLink, IconUpload, IconX } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import { Alert, Button, Card, Group, Stack, Text, ThemeIcon } from "@/components/ui";
import { validarParejaCfdi, type ResultadoValidacion } from "../utils/validar-pareja";

/**
 * Dropzone DUAL (CFDI XML + PDF) · el usuario debe elegir una pareja con el
 * mismo nombre base (cambia sólo la extensión). Cliente valida:
 *
 *   1. Extensión correcta de cada archivo.
 *   2. Header del PDF empieza con `%PDF-` (no corrupto).
 *   3. XML contiene `<cfdi:Comprobante Version="4.0"`.
 *   4. Nombre base de ambos coincide ignoring case.
 *
 * Si todo OK · habilita "Validar CFDI" y dispara la validación remota
 * (reglas RN1-RN10 + SAT) más la carga del PDF como soporte documental.
 */
export function CFDIPdfDropzone({
  cargando,
  esperandoSat,
  onSubmit,
}: {
  cargando: boolean;
  esperandoSat: boolean;
  onSubmit: (xml: File, pdf: File) => void;
}) {
  const ambosInputRef = useRef<HTMLInputElement>(null);

  const [xml, setXml] = useState<File | null>(null);
  const [pdf, setPdf] = useState<File | null>(null);
  const [validacion, setValidacion] = useState<ResultadoValidacion | null>(null);
  const [dragActivo, setDragActivo] = useState(false);
  const ocupado = cargando || esperandoSat;

  /**
   * Reparte una lista de archivos a los slots por extensión · permite
   * seleccionar/soltar el XML y el PDF a la vez. No pisa un slot si en la
   * selección no viene ese tipo (así el usuario puede reemplazar solo uno).
   */
  const distribuirArchivos = (files: File[]) => {
    const xmlFile = files.find((f) => f.name.toLowerCase().endsWith(".xml"));
    const pdfFile = files.find((f) => f.name.toLowerCase().endsWith(".pdf"));
    if (xmlFile) setXml(xmlFile);
    if (pdfFile) setPdf(pdfFile);
  };

  // Re-valida cada vez que cambian los archivos · async porque lee header.
  useEffect(() => {
    let activo = true;
    validarParejaCfdi(xml, pdf).then((r) => {
      if (activo) setValidacion(r);
    });
    return () => {
      activo = false;
    };
  }, [xml, pdf]);

  const handleXmlSeleccionado = (file: File | null) => setXml(file);
  const handlePdfSeleccionado = (file: File | null) => setPdf(file);

  const v = validacion;

  return (
    <Card padding="lg">
      <Stack gap="md">
        <Stack gap={4}>
          <Text fw={600}>Zona de carga · CFDI + PDF</Text>
          <Text size="sm" c="dimmed">
            Selecciona el XML y el PDF del CFDI · ambos deben tener el mismo nombre (sólo cambia la extensión). Puedes elegir los dos a la vez o
            arrastrarlos aquí. El sistema validará el XML contra las 10 reglas RN1-RN10, lo consultará en el SAT y subirá el PDF como soporte una
            vez validado.
          </Text>
        </Stack>

        <Button variant="light" leftSection={<IconFiles size={16} />} onClick={() => ambosInputRef.current?.click()} disabled={ocupado} className="self-start">
          Seleccionar XML y PDF
        </Button>

        <div
          onDragOver={(e) => {
            e.preventDefault();
            if (!ocupado) setDragActivo(true);
          }}
          onDragLeave={() => setDragActivo(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragActivo(false);
            if (!ocupado) distribuirArchivos(Array.from(e.dataTransfer.files));
          }}
          className="rounded-xl outline-offset-4 transition-[outline-color] duration-120"
          style={{ outline: dragActivo ? "2px dashed var(--color-multiva-500)" : "2px dashed transparent" }}
        >
          <div className="grid grid-cols-1 items-stretch gap-3 sm:grid-cols-2">
            <ArchivoSlot tipo="xml" archivo={xml} error={v?.errorXml} ocupado={ocupado} onQuitar={() => handleXmlSeleccionado(null)} />
            <ArchivoSlot tipo="pdf" archivo={pdf} error={v?.errorPdf} ocupado={ocupado} onQuitar={() => handlePdfSeleccionado(null)} />
          </div>
        </div>

        {v?.errorPareja ? (
          <Alert variant="warning" icon={<IconLink size={14} />} title="Los nombres no coinciden">
            {v.errorPareja}
          </Alert>
        ) : null}

        {/* Oculto visualmente pero renderizado (no display:none) · algunos
            navegadores no abren el diálogo nativo con .click() sobre un input
            display:none. */}
        <input
          ref={ambosInputRef}
          type="file"
          accept=".xml,application/xml,text/xml,.pdf,application/pdf"
          multiple
          onChange={(e) => {
            distribuirArchivos(Array.from(e.currentTarget.files ?? []));
            // Permite volver a elegir los mismos archivos (reset del input).
            e.currentTarget.value = "";
          }}
          className="absolute h-px w-px overflow-hidden opacity-0"
          style={{ pointerEvents: "none" }}
          tabIndex={-1}
          aria-hidden="true"
        />

        <Group justify="space-between">
          <Text size="xs" c="dimmed">
            CFDI 4.0 · tipo Ingreso · cualquier moneda del catálogo SAT (MXN, USD, EUR, …) · PDF acompañante.
          </Text>
          <Button
            leftSection={<IconUpload size={14} />}
            disabled={!v?.ok || ocupado}
            loading={cargando}
            onClick={() => {
              if (xml && pdf && v?.ok) onSubmit(xml, pdf);
            }}
          >
            {cargando ? "Subiendo XML…" : esperandoSat ? "Validando con SAT…" : "Validar CFDI"}
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

function ArchivoSlot({
  tipo,
  archivo,
  error,
  ocupado,
  onQuitar,
}: {
  tipo: "xml" | "pdf";
  archivo: File | null;
  error: string | null | undefined;
  ocupado: boolean;
  onQuitar: () => void;
}) {
  const esXml = tipo === "xml";
  const Icon = esXml ? IconFileTypeXml : IconFileTypePdf;

  const tieneArchivo = !!archivo;
  const valido = tieneArchivo && !error;
  const colorBorder = valido ? "var(--color-info-600)" : error ? "var(--color-peligro-600)" : "var(--color-border)";

  return (
    <Card
      padding="md"
      className="cursor-default transition-colors duration-150"
      style={{
        border: `2px ${tieneArchivo ? "solid" : "dashed"} ${colorBorder}`,
        background: tieneArchivo ? "color-mix(in srgb, var(--color-info-600) 8%, transparent)" : "transparent",
      }}
    >
      <Stack gap="sm" align="center" className="text-center">
        <ThemeIcon size="xl" radius="xl" variant="light" color={valido ? "info" : error ? "peligro" : "slate"}>
          {valido ? <IconCheck size={24} /> : <Icon size={24} stroke={1.5} />}
        </ThemeIcon>
        <Stack gap={2}>
          <Text fw={600} size="sm">
            {esXml ? "CFDI XML" : "PDF del CFDI"}
          </Text>
          {tieneArchivo ? (
            <>
              <Text size="xs" c="dimmed" truncate className="max-w-60">
                {archivo!.name}
              </Text>
              <Text size="xs" c="dimmed">
                {(archivo!.size / 1024).toFixed(1)} KB
              </Text>
            </>
          ) : (
            <Text size="xs" c="dimmed">
              Pendiente · {esXml ? ".xml" : ".pdf"}
            </Text>
          )}
        </Stack>
        {error ? (
          <Text size="xs" c="peligro-600" fw={500} className="max-w-65">
            {error}
          </Text>
        ) : null}
        {tieneArchivo && !ocupado ? (
          <Button
            variant="subtle"
            size="xs"
            leftSection={<IconX size={12} />}
            onClick={(e) => {
              e.stopPropagation();
              onQuitar();
            }}
          >
            Quitar
          </Button>
        ) : null}
      </Stack>
    </Card>
  );
}
